import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowUp, List, Star, Tv, Film, Headphones, Users, Sparkles, Play, Trophy, Heart } from 'lucide-react';
import api, { hasStoredToken } from '../lib/api';

// Custom Sorta Logo component
const SortaLogo = ({ className = "", size = "default" }) => {
  const sizeClasses = {
    small: "h-8",
    default: "h-12",
    large: "h-16"
  };
  
  return (
    <img 
      src="https://customer-assets.emergentagent.com/job_series-ranker-1/artifacts/barwtr5h_image.jpg"
      alt="Sorta Media"
      className={`${sizeClasses[size]} object-contain rounded-lg ${className}`}
    />
  );
};

// Floating animated icon component
const FloatingIcon = ({ icon: Icon, className, delay = 0 }) => (
  <div 
    className={`absolute opacity-20 animate-float ${className}`}
    style={{ animationDelay: `${delay}s` }}
  >
    <Icon className="w-12 h-12 text-white" />
  </div>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  
  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      // Quick check - if no stored token and cookies likely blocked, skip auth check
      if (!hasStoredToken() && !document.cookie.includes('session_token')) {
        setIsCheckingAuth(false);
        return;
      }
      
      try {
        const response = await api.get('/auth/me');
        if (response.data?.user_id) {
          navigate('/social');
        }
      } catch {
        // eslint-disable-next-line no-empty -- Intentional: Auth check failure is expected for new visitors, stay on landing page
      } finally {
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, [navigate]);
  
  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-pulse">
          <SortaLogo size="large" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Add custom animation styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }
        .stagger-1 { animation-delay: 0.1s; }
        .stagger-2 { animation-delay: 0.2s; }
        .stagger-3 { animation-delay: 0.3s; }
        .stagger-4 { animation-delay: 0.4s; }
      `}</style>

      {/* Hero Section */}
      <div 
        className="relative min-h-[95vh] flex flex-col overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(248, 113, 113, 0.95) 0%, rgba(61, 79, 111, 0.92) 100%), url('https://images.unsplash.com/photo-1536440136628-849c177e76a1?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Floating Media Icons */}
        <FloatingIcon icon={Tv} className="top-[15%] left-[8%]" delay={0} />
        <FloatingIcon icon={Film} className="top-[25%] right-[12%]" delay={1.5} />
        <FloatingIcon icon={Headphones} className="top-[60%] left-[5%]" delay={3} />
        <FloatingIcon icon={Play} className="top-[45%] right-[8%]" delay={2} />
        <FloatingIcon icon={Star} className="top-[70%] right-[15%]" delay={4} />
        
        {/* Navigation */}
        <nav className="container mx-auto px-6 py-6 flex items-center justify-between relative z-10">
          <div className="flex items-center gap-3">
            <SortaLogo size="default" />
            <span className="hidden sm:block text-white font-outfit font-semibold text-xl">Media</span>
          </div>
          <Button
            data-testid="login-btn-nav"
            onClick={handleLogin}
            className="bg-white text-[#3D4F6F] hover:bg-gray-100 rounded-full px-6 py-2 font-semibold"
          >
            Sign In
          </Button>
        </nav>
        
        {/* Hero Content */}
        <div className="flex-1 container mx-auto px-6 flex flex-col items-center justify-center text-center relative z-10 pb-32">
          {/* Badge */}
          <div className="flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-2 mb-6 animate-fade-in-up">
            <Sparkles className="w-4 h-4 text-white" />
            <span className="text-white/90 text-sm font-medium">Discover • Organize • Rank • Share</span>
          </div>
          
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in-up stagger-1">
            Your Media,
            <br />
            <span className="text-[#3D4F6F]">Ranked Your Way</span>
          </h1>
          
          <p className="text-white/90 text-base sm:text-lg max-w-2xl mb-10 animate-fade-in-up stagger-2 leading-relaxed">
            The ultimate platform to discover, organize, and rank all your entertainment. 
            Create personal or collaborative lists for TV shows, movies, and podcasts — 
            then watch your unique taste profile emerge.
          </p>
          
          <Button
            data-testid="get-started-btn"
            onClick={handleLogin}
            className="bg-white text-[#F87171] hover:bg-gray-100 rounded-full px-10 py-4 text-lg font-bold shadow-xl hover:shadow-2xl transition-all animate-fade-in-up stagger-3"
          >
            Get Started Free
          </Button>
          
          {/* Feature Preview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 max-w-4xl animate-fade-in-up stagger-4">
            <FeatureCard
              icon={<List className="w-6 h-6" />}
              title="Create Lists"
              description="Organize your media into personal or shared watchlists"
            />
            <FeatureCard
              icon={<ArrowUp className="w-6 h-6" />}
              title="Rank Everything"
              description="Drag and drop to prioritize what to experience next"
            />
            <FeatureCard
              icon={<Star className="w-6 h-6" />}
              title="Discover Your Score"
              description="Get personalized scores based on your unique rankings"
            />
          </div>
        </div>
        
        {/* Wave divider - adjusted position */}
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-auto">
            <path d="M0 100L60 87.5C120 75 240 50 360 37.5C480 25 600 25 720 33.3C840 41.7 960 58.3 1080 62.5C1200 66.7 1320 58.3 1380 54.2L1440 50V100H1380C1320 100 1200 100 1080 100C960 100 840 100 720 100C600 100 480 100 360 100C240 100 120 100 60 100H0Z" fill="#F8FAFC"/>
          </svg>
        </div>
      </div>
      
      {/* What You Can Track Section */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="container mx-auto px-6">
          <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-[#3D4F6F] text-center mb-4">
            All Your Media, One Place
          </h2>
          <p className="text-[#64748B] text-center mb-12 max-w-xl mx-auto">
            Track and rank everything you watch and listen to
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <MediaTypeCard
              icon={<Tv className="w-8 h-8" />}
              title="TV Shows"
              description="Binge-worthy series, limited runs, and everything in between"
              color="bg-purple-500"
            />
            <MediaTypeCard
              icon={<Film className="w-8 h-8" />}
              title="Movies"
              description="Blockbusters, indie gems, classics, and new releases"
              color="bg-[#F87171]"
            />
            <MediaTypeCard
              icon={<Headphones className="w-8 h-8" />}
              title="Podcasts"
              description="True crime, comedy, interviews, and deep dives"
              color="bg-[#3D4F6F]"
            />
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-[#3D4F6F] text-center mb-4">
            How It Works
          </h2>
          <p className="text-[#64748B] text-center mb-16 max-w-xl mx-auto">
            A smarter way to track and rank your media journey
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <StepCard
              number="1"
              title="Create a List"
              description="Build personal watchlists or invite friends to create joint lists together"
            />
            <StepCard
              number="2"
              title="Rank Your Priority"
              description="Drag to reorder and set what you'll watch or listen to first"
            />
            <StepCard
              number="3"
              title="Mark as Complete"
              description="Grade each item: Liked it, It was alright, or Didn't like it"
            />
            <StepCard
              number="4"
              title="See Your Scores"
              description="After 10 items, unlock personalized scores based on your rankings"
            />
          </div>
        </div>
      </section>
      
      {/* Better Together Section */}
      <section className="py-20 bg-gradient-to-br from-[#3D4F6F] to-[#2D3A4F]">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-6 h-6 text-[#F87171]" />
                <span className="text-[#F87171] font-semibold">Better Together</span>
              </div>
              <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-6">
                Collaborative Lists for Friends & Family
              </h2>
              <p className="text-white/80 mb-8 leading-relaxed">
                Create joint lists and invite others to contribute their picks. 
                Discover what you have in common, see combined rankings, and 
                find your next group watch or listen. Perfect for movie nights, 
                podcast clubs, or tracking shows with a partner.
              </p>
              <ul className="space-y-4">
                <JointFeatureItem text="Share a link to invite collaborators" />
                <JointFeatureItem text="See combined rankings across all members" />
                <JointFeatureItem text="Discover common favorites automatically" />
                <JointFeatureItem text="Each member maintains their own rankings" />
              </ul>
            </div>
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex -space-x-3">
                    <div className="w-10 h-10 rounded-full bg-[#F87171] flex items-center justify-center text-white font-bold border-2 border-white/20">A</div>
                    <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold border-2 border-white/20">B</div>
                    <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold border-2 border-white/20">C</div>
                  </div>
                  <span className="text-white font-medium">3 members</span>
                </div>
                <h3 className="text-white font-outfit font-semibold text-xl mb-4">Movie Night Picks</h3>
                <div className="space-y-3">
                  <MockListItem rank={1} title="Dune: Part Two" votes={3} />
                  <MockListItem rank={2} title="Oppenheimer" votes={3} />
                  <MockListItem rank={3} title="Poor Things" votes={2} />
                  <MockListItem rank={4} title="Barbie" votes={2} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-[#F87171]">
        <div className="container mx-auto px-6 text-center">
          <Trophy className="w-16 h-16 text-white/30 mx-auto mb-6" />
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Sorta Your Media?
          </h2>
          <p className="text-white/90 mb-10 max-w-xl mx-auto">
            Join thousands of media enthusiasts who've discovered their true entertainment preferences.
          </p>
          <Button
            data-testid="cta-get-started-btn"
            onClick={handleLogin}
            className="bg-white text-[#F87171] hover:bg-gray-100 rounded-full px-10 py-4 text-lg font-bold shadow-xl hover:shadow-2xl transition-all"
          >
            Start Ranking Now
          </Button>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-8 bg-[#1E293B]">
        <div className="container mx-auto px-6 flex flex-col md:flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <SortaLogo size="small" />
            <span className="text-white/60 font-outfit">Media</span>
          </div>
          <p className="text-[#64748B] text-sm mt-4 md:mt-0">
            © 2026 Sorta Media. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-left border border-white/20 hover:bg-white/15 transition-all">
      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-white mb-4">
        {icon}
      </div>
      <h3 className="font-outfit font-semibold text-white text-lg mb-2">{title}</h3>
      <p className="text-white/70 text-sm">{description}</p>
    </div>
  );
}

function MediaTypeCard({ icon, title, description, color }) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-slate-100 text-center group">
      <div className={`w-16 h-16 ${color} rounded-2xl flex items-center justify-center text-white mx-auto mb-4 group-hover:scale-110 transition-transform`}>
        {icon}
      </div>
      <h3 className="font-outfit font-semibold text-[#3D4F6F] text-xl mb-2">{title}</h3>
      <p className="text-[#64748B] text-sm">{description}</p>
    </div>
  );
}

function StepCard({ number, title, description }) {
  return (
    <div className="text-center">
      <div className="w-16 h-16 bg-[#F87171] text-white rounded-full flex items-center justify-center mx-auto mb-6 font-outfit font-bold text-2xl shadow-lg">
        {number}
      </div>
      <h3 className="font-outfit font-semibold text-[#3D4F6F] text-xl mb-3">{title}</h3>
      <p className="text-[#64748B]">{description}</p>
    </div>
  );
}

function JointFeatureItem({ text }) {
  return (
    <li className="flex items-center gap-3 text-white/80">
      <div className="w-6 h-6 rounded-full bg-[#F87171]/20 flex items-center justify-center flex-shrink-0">
        <Heart className="w-3 h-3 text-[#F87171]" />
      </div>
      {text}
    </li>
  );
}

function MockListItem({ rank, title, votes }) {
  return (
    <div className="flex items-center gap-3 bg-white/5 rounded-xl p-3">
      <span className="w-8 h-8 bg-[#F87171]/20 rounded-lg flex items-center justify-center text-[#F87171] font-bold text-sm">
        {rank}
      </span>
      <span className="text-white flex-1 truncate">{title}</span>
      <span className="text-white/50 text-sm">{votes}/3</span>
    </div>
  );
}
